// НАПРАВЛЕНИЕ И ВЫСОТА ОКОН СЦЕНАРИЯ — В ОДНОМ МЕСТЕ, чтобы вид и анимация у всех окон
// были едиными, а правка делалась одной строкой. Веб читает результат через CSS
// (класс .sheet и переменная --sheet-top у .drawer, cabinet.css), натив считает то же
// самое сам по детенту (scriptera-app/ios/App/App/WebScreenTransition.swift →
// LayerGeom.sheetFrame). Направление шторки едино для мобильного сайта и приложения —
// канон NATIVE-SLIDE-PLAN §11 п.9.
//
// ИСТОРИЯ РЕШЕНИЙ (чтобы будущий читатель понимал, почему так):
//  • 31.07.2026, К35 — все окна страницы сценария стали ВЕРТИКАЛЬНЫМИ листами, открытыми
//    НЕ ДО ВЕРХА (детент 0.88): сверху нарочно оставалась полоса схемы. Десктоп владелец
//    той же правкой оставил прежним (плавающая панель, вид ChatPlace).
//  • 13.08.2026 — окна КОНСТРУКТОРА отделили и перевели на ГОРИЗОНТАЛЬНЫЙ выезд справа
//    (набор EDITOR_SIDE), листом К35 остались окна страницы сценария.
//  • 25.08.2026, УТРО — владелец попросил попробовать всё вертикальным: «все добавления и
//    редактирования условий внутри конструктора, все модалки тоже сделать вертикальными.
//    И открывать их повыше — почти до островка». EDITOR_SIDE сняли, детент 0.88 убрали.
//  • 25.08.2026, ВЕЧЕР — посмотрел живьём и РАЗДЕЛИЛ по смыслу (правило HIG Apple, тот же
//    ход у Telegram): ВЕРТИКАЛЬНАЯ шторка = НОВЫЙ КОНТЕКСТ (родитель уходит назад колодой),
//    а шаги ВГЛУБЬ ВНУТРИ контекста идут ГОРИЗОНТАЛЬНО, в пределах той же карты. Значит:
//     — правка блока (настройка шага, каталоги условий и действий, «Добавить шаг», редакторы
//       текста и кнопки, «Добавить контент», заметка, довесок-модель, подключение и пикер
//       аккаунта, канал) — набор EDITOR_SIDE, выезд справа. Это шаг внутри схемы, а не новый
//       контекст: сама схема остаётся тем же экраном.
//     — переход в шаг с несколькими входами (шторка ленты, «новый уровень схемы») и окна
//       СТРАНИЦЫ сценария («О сценарии», «Поделиться», «Запуски», карточка бота) — набор
//       EDITOR_SHEET, вертикальный лист под островок; под ним схема уходит назад колодой.
//     — правка блока ИЗ шторки ленты — снова EDITOR_SIDE, но В ПРЕДЕЛАХ ЭТОЙ КАРТЫ: в
//       приложении натив кладёт вложенный боковой слой в раму листа, в вебе боковой уровень
//       ложится стопкой поверх живого листа (drawer.tsx).
//    Высота листа осталась утренняя — ШТАТНЫЙ ЛИСТ БЕЗ ДЕТЕНТА, верхняя кромка сразу под
//    вырезом/островком (в вебе env(safe-area-inset-top)+12px, в приложении
//    safeAreaInsets.top + sheetGap).

/** Верхняя кромка листа для CSS по детенту. Вне (0;1) — null: детента нет, лист ШТАТНЫЙ
 *  (верх под вырезом — «почти до островка», владелец 25.08.2026). 0.4 (подтверждения,
 *  класс .short) даёт ровно прежние 60dvh — старые вызовы не меняют вида. */
export function sheetTopFromDetent(detent?: number | null): string | null {
  if (typeof detent !== 'number' || !Number.isFinite(detent)) return null;
  if (detent <= 0 || detent >= 1) return null;
  // Округление до 0.1dvh: 0.4 → 60dvh, 0.55 → 45dvh (без хвостов плавающей точки).
  const top = Math.round((1 - detent) * 1000) / 10;
  return `${top}dvh`;
}

/** Набор полей DrawerPayload для окон, которые открывают НОВЫЙ КОНТЕКСТ: окна СТРАНИЦЫ
 *  сценария («О сценарии», «Поделиться», «Запуски», «что вышло у шага», окна карточки бота)
 *  и ШТОРКА ЛЕНТЫ — переход в шаг с несколькими входами, то есть новый уровень схемы
 *  (владелец 25.08.2026, вечер).
 *
 *  ВЕРТИКАЛЬНЫЙ ЛИСТ СНИЗУ, БЕЗ ДЕТЕНТА — верх сразу под вырезом («почти до островка»),
 *  одинаково на мобильном сайте и в приложении (направление едино, §11 п.9). Детента в
 *  наборе НЕТ намеренно: прежние 0.88 оставляли лист на 12% экрана ниже, и владелец
 *  25.08 попросил поднять. Вглубь — СТЕК УРОВНЕЙ по `winKey`: новый лист ложится поверх
 *  ЖИВОГО нижнего, «←»/свайп-вниз снимают только верхний, «✕» есть только на корневом
 *  (drawer.tsx в вебе, стопка родных листов в приложении — native-layer-host.tsx). В
 *  приложении под таким листом экран уходит назад КОЛОДОЙ (LayerGeom.deck*, §11 п.18).
 *
 *  ОКНА КОНСТРУКТОРА этим набором НЕ открываются (владелец 25.08.2026, вечер): правка
 *  блока — шаг ВГЛУБЬ внутри того же контекста, ей набор EDITOR_SIDE ниже.
 *
 *  ДЕСКТОП листом НЕ становится (владелец 2026-07-31 отклонил эту часть К35, подтверждено
 *  Г1 18.08.2026): поля sheet/grab действуют только в мобильном медиазапросе, на широком
 *  экране такое окно — обычная плавающая панель кабинета (§11 п.9б). */
export const EDITOR_SHEET = {
  sheet: true,
} as const;

/** Набор полей DrawerPayload для ЛЮБОГО окна КОНСТРУКТОРА — правки блока (пикер блоков,
 *  каталоги условий и действий, настройка шага и условия, редакторы текста и кнопки,
 *  «Добавить контент», заметка, довесок-модель, подключение и пикер аккаунта, канал):
 *  ГОРИЗОНТАЛЬНЫЙ слой во всю высоту, выезжающий СПРАВА — и на телефоне, и в приложении
 *  (решение владельца 13.08.2026, ВОЗВРАЩЁННОЕ вечером 25.08.2026 после дня вертикали).
 *
 *  ПОЧЕМУ ГОРИЗОНТАЛЬ (владелец 25.08.2026, вечер, правило HIG Apple): вертикальная шторка
 *  означает НОВЫЙ КОНТЕКСТ — родитель уходит назад колодой; правка блока новым контекстом
 *  не является, это шаг ВГЛУБЬ внутри схемы, и он обязан идти в пределах ТОЙ ЖЕ карты.
 *  Отсюда же поведение внутри шторки ленты: окно конструктора, открытое из листа, едет
 *  справа В РАМЕ ЭТОГО ЛИСТА (в приложении — вложенный боковой слой натива, в вебе —
 *  боковой уровень стопкой поверх живого листа), а лист под ним остаётся собой.
 *
 *  Вглубь — новый уровень тем же выездом (`winKey`); «назад» на вложенном уровне возвращает
 *  НА УРОВЕНЬ ВЫШЕ, «закрыть всё» есть только на корневом (drawer.tsx, стек уровней).
 *
 *  ДЕСКТОП (владелец 18.08.2026, Г1): вид и жест ТЕ ЖЕ — окно конструктора на широком
 *  экране это обычная плавающая панель кабинета с выездом справа, и уровни вглубь едут тем
 *  же выездом. В ПРИЛОЖЕНИИ отсутствие `sheet` означает боковой родной слой — его рисует и
 *  двигает натив (ScripteraViewController.layerPush, bottom: false). */
export const EDITOR_SIDE = { side: true } as const;

/** ПОЛУЛИСТ — родной детент `.medium()` / низкий лист вопроса (`WizAskSheet`) в браузере:
 *  52 % экрана, то же число, что у листов триггера мастера (`WIZ_HALF_SHEET`) и листа
 *  условий аудитории. Окно «Сообщение»: запись голосового, «Отрезок», вопрос о составе
 *  (кадры iOS M44/M45/M47, круг 3 03.10). */
export const HALF_SHEET = 0.52;
